/**
 * RoutingMemoryCard — Settings → Document Scanner → Routing Memory tab.
 *
 * Lists every routing rule AI Buck has learned (and any the firm has
 * authored manually) so power users can:
 *   - Disable a rule that's no longer accurate
 *   - Change the destination
 *   - Hard-delete a rule
 *   - Add a new manual rule
 *
 * Mount this anywhere a Settings/Workspace context provides
 * `currentWorkspace`. Typical mount: <RoutingMemoryCard currentWorkspace={ws} />
 */
import { useEffect, useState, useCallback } from 'react';
import { Brain, Plus, Trash2, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { apiFetch } from '../../fetchInterceptor';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { value: 'bill',           label: 'Bill' },
  { value: 'invoice',        label: 'Invoice' },
  { value: 'receipt',        label: 'Receipt' },
  { value: 'tax_return',     label: 'Tax Return' },
  { value: 'w9_form',        label: 'W-9 Form' },
  { value: 'contract',       label: 'Contract' },
  { value: 'bank_statement', label: 'Bank Statement' },
  { value: 'unknown',        label: 'Any / Unknown' },
];

const DESTINATIONS = [
  { value: 'tax_folder', label: 'Tax Returns' },
  { value: 'receipts',   label: 'Receipts' },
  { value: 'banking',    label: 'Bank Statements / Banking' },
  { value: 'vendors',    label: 'Vendor Records' },
  { value: 'documents',  label: 'Scanned Documents' },
];

const fmtRelative = (iso) => {
  if (!iso) return 'never';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
};

export default function RoutingMemoryCard({ currentWorkspace }) {
  const wsId = currentWorkspace?.workspace_id;
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newRule, setNewRule] = useState({ vendor_label: '', doc_type: 'receipt', destination: 'receipts' });

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/api/ocr-routing/rules?workspace_id=${wsId}`);
      if (r.ok) {
        const data = await r.json();
        setRules(data.rules || []);
      }
    } finally {
      setLoading(false);
    }
  }, [wsId]);
  useEffect(() => { load(); }, [load]);

  const toggleEnabled = async (rule_id, enabled) => {
    const r = await apiFetch(`${API_URL}/api/ocr-routing/rules/${rule_id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    });
    if (!r.ok) { toast.error('Failed to update rule'); return; }
    toast.success(enabled ? 'Rule enabled' : 'Rule disabled');
    load();
  };

  const changeDestination = async (rule_id, destination) => {
    const r = await apiFetch(`${API_URL}/api/ocr-routing/rules/${rule_id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ destination }),
    });
    if (!r.ok) { toast.error('Failed to change destination'); return; }
    toast.success('Destination updated');
    load();
  };

  const removeRule = async (rule_id) => {
    if (!window.confirm('Forget this routing rule? Future scans from this vendor will need manual filing.')) return;
    const r = await apiFetch(`${API_URL}/api/ocr-routing/rules/${rule_id}`, { method: 'DELETE' });
    if (!r.ok) { toast.error('Failed to delete rule'); return; }
    toast.success('Rule removed');
    load();
  };

  const submitNewRule = async () => {
    if (!newRule.vendor_label.trim()) { toast.error('Vendor name is required'); return; }
    setAdding(true);
    try {
      const r = await apiFetch(`${API_URL}/api/ocr-routing/rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: wsId, ...newRule, vendor_label: newRule.vendor_label.trim() }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to add rule');
        return;
      }
      toast.success('Routing rule added');
      setShowAdd(false);
      setNewRule({ vendor_label: '', doc_type: 'receipt', destination: 'receipts' });
      load();
    } finally {
      setAdding(false);
    }
  };

  if (loading) return <div className="animate-pulse h-48 bg-muted rounded-2xl" />;

  return (
    <div className="rounded-2xl border bg-card p-6" data-testid="routing-memory-card">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Brain className="w-5 h-5 text-violet-600" />
            Routing Memory
            <span className="px-1.5 py-0.5 rounded-md bg-violet-500/15 text-[10px] font-semibold text-violet-700 dark:text-violet-400 ring-1 ring-violet-500/30 inline-flex items-center gap-0.5">
              <Sparkles className="w-2.5 h-2.5" /> New
            </span>
          </h3>
          <p className="text-sm text-muted-foreground mt-0.5 max-w-xl">
            AI Buck remembers where you file each vendor and suggests the same destination next time. Disable a rule below to stop the suggestion.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowAdd(true)} data-testid="add-routing-rule-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add rule
        </Button>
      </div>

      {rules.length === 0 ? (
        <div className="mt-6 text-center py-8 px-4 rounded-xl bg-muted/30">
          <Brain className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm text-muted-foreground">
            No routing rules yet. AI Buck will start learning as you file scanned documents.
          </p>
        </div>
      ) : (
        <div className="mt-5 space-y-2">
          {rules.map((rule) => {
            const destMeta = DESTINATIONS.find((d) => d.value === rule.destination) || {};
            const typeMeta = DOC_TYPES.find((d) => d.value === rule.doc_type) || {};
            return (
              <div
                key={rule.rule_id}
                className="rounded-lg border p-3 flex items-center gap-3"
                data-testid={`routing-rule-${rule.rule_id}`}
              >
                <Switch
                  checked={!!rule.enabled}
                  onCheckedChange={(c) => toggleEnabled(rule.rule_id, c)}
                  data-testid={`routing-rule-toggle-${rule.rule_id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {rule.vendor_label || rule.vendor_signature}
                    <span className="text-muted-foreground"> · {typeMeta.label || rule.doc_type}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground flex flex-wrap gap-2 mt-0.5">
                    <Badge variant="outline" className="text-[10px]">
                      {rule.source === 'manual' ? 'Manual' : `Learned · ${rule.hit_count || 0} scans`}
                    </Badge>
                    {rule.auto_apply && (
                      <Badge variant="outline" className="text-[10px] bg-violet-50 dark:bg-violet-950/40 border-violet-200 dark:border-violet-900 text-violet-700 dark:text-violet-400">
                        Auto-suggests
                      </Badge>
                    )}
                    <span>Last used {fmtRelative(rule.last_used_at)}</span>
                  </div>
                </div>
                <Select
                  value={rule.destination}
                  onValueChange={(v) => changeDestination(rule.rule_id, v)}
                  disabled={!rule.enabled}
                >
                  <SelectTrigger className="w-44 h-8 text-xs" data-testid={`routing-rule-dest-${rule.rule_id}`}>
                    <SelectValue>{destMeta.label || rule.destination}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {DESTINATIONS.map((d) => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeRule(rule.rule_id)}
                  className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`routing-rule-delete-${rule.rule_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add-rule dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-routing-rule-dialog">
          <DialogHeader>
            <DialogTitle>Add a routing rule</DialogTitle>
            <DialogDescription>
              Tell AI Buck where to suggest filing scans from a specific vendor and doc type.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="rule-vendor">Vendor / sender</Label>
              <Input
                id="rule-vendor"
                value={newRule.vendor_label}
                onChange={(e) => setNewRule({ ...newRule, vendor_label: e.target.value })}
                placeholder="e.g. Shell, ADP Payroll, IRS"
                data-testid="routing-rule-vendor-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Doc type</Label>
                <Select value={newRule.doc_type} onValueChange={(v) => setNewRule({ ...newRule, doc_type: v })}>
                  <SelectTrigger data-testid="routing-rule-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>File to</Label>
                <Select value={newRule.destination} onValueChange={(v) => setNewRule({ ...newRule, destination: v })}>
                  <SelectTrigger data-testid="routing-rule-dest-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DESTINATIONS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={submitNewRule} disabled={adding} data-testid="routing-rule-submit">
              {adding ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              Add rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
